import * as React from "react";
import { observer } from "mobx-react";
import { withStores } from "stores";
import { EnforceUserSignInFlexModuleResult, EnforceUserSignInProps } from "./typings";
import { UitkCard } from "uitk-react-cards";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkSecondaryButton } from "uitk-react-button";
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkIcon, UitkIconSize } from "uitk-react-icons";
import { useLocalization } from "@shared-ui/localization-context";
import { FlexClickTracker } from "components/utility/analytics/FlexClickTracker/FlexClickTracker";
import { useClickTracker } from "@shared-ui/clickstream-analytics-context";
import {
  Action,
  FlexTrackingInfo,
  sendImmediateClickstreamTrackEvent,
} from "components/utility/analytics/FlexAnalyticsUtils";

const ButtonTracked = FlexClickTracker(UitkSecondaryButton);
const signInModuleName = "Partner.Account.SignIn";
const flexTracking: FlexTrackingInfo = {
  moduleName: signInModuleName,
  rfrr: "Presented",
  action: Action.IMPRESSION,
};

export const EnforceUserSignIn = withStores(
  "flexModuleModelStore",
  "context"
)(
  observer((props: EnforceUserSignInProps) => {
    const { templateComponent, flexModuleModelStore } = props;

    /* istanbul ignore if */
    if (!templateComponent || !flexModuleModelStore) {
      return null;
    }

    const { metadata } = templateComponent;
    const { id } = metadata;
    const model = flexModuleModelStore.getModel(id) as EnforceUserSignInFlexModuleResult | null;
    const { formatText } = useLocalization();

    if (!model) {
      return null;
    }
    const { signInURL, createAccountURL, title, subheading } = model;
    const signInLabel = formatText("rewardsBanner.signIn");
    const joinNowLabel = formatText("rewardsBanner.joinNow");
    const track = useClickTracker();

    React.useEffect(() => {
      sendImmediateClickstreamTrackEvent(flexTracking, track);
    }, [track]);

    return (
      <UitkCard border padded className="sign-in-card">
        <UitkLayoutFlex direction="column" space="three">
          <UitkLayoutFlexItem alignSelf="center">
            <UitkIcon name="account_circle" size={UitkIconSize.LARGE} theme="default" />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem alignSelf="center">
            <UitkHeading align="center" size={6} tag="h1" data-testid="heading">
              {title}
            </UitkHeading>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem alignSelf="center">
            <UitkText align="center" weight="regular" data-testid="sub-heading">
              {subheading}
            </UitkText>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <UitkLayoutFlex space="two" wrap="wrap">
              <UitkLayoutFlexItem grow={1} minWidth="sixteen">
                <ButtonTracked href={signInURL} data-testid="sign-in-button" moduleName={signInModuleName} rfrr="Login">
                  {signInLabel}
                </ButtonTracked>
              </UitkLayoutFlexItem>
              {createAccountURL && (
                <UitkLayoutFlexItem grow={1} minWidth="sixteen">
                  <ButtonTracked
                    href={createAccountURL}
                    data-testid="create-account-button"
                    moduleName={signInModuleName}
                    rfrr="Join"
                  >
                    {joinNowLabel}
                  </ButtonTracked>
                </UitkLayoutFlexItem>
              )}
            </UitkLayoutFlex>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </UitkCard>
    );
  })
);

export default EnforceUserSignIn;
